/* tslint:disable:max-file-line-count */

import { parseString, Builder } from 'xml2js';
import { DeviceIndex, GraphType, HeatMapType, ReportOutput, ScheduleType, ScheduleTypes, Size, UtcTime } from 'types';
import { ReportDevice } from './ReportDevice';
import { HeatMapZone } from './HeatMapZone';
const u = require('updeep');

// StartEnd is stored in minutes since 0/0/0. Define 1970/1/1 in minutes to facilitate translation
const epochMinutes = 1035593280;
const epochTicks = 621355968000000000;
const millisecondsPerMinute = 60000;
const ticksPerMillisecond = 10000;

const builder = new Builder({
    renderOpts: {
        pretty: false,
        indent: '',
        newline: '',
    },
    headless: true,
});

export type ReportSettingsChangeHandler = (fn: ((settings: Readonly<ReportSettings>) => Readonly<ReportSettings>)) => void;

export type Fence = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
};

export type TableColumn = {
    name: string;
    visibleIndex?: number;
    groupIndex?: number;
    sortIndex?: number;
    sortAsc?: boolean;
};

export type ScheduleRangeEndType = 'never' | 'on' | 'after';

type NodeWithId = {
    $: {
        ID: string;
    };
};

type FenceNode = {
    $: {
        x1: string;
        y1: string;
        x2: string;
        y2: string;
    };
};

type ValueNode = {
    $: {
        Value: string;
    };
};

type TableColumnNode = {
    $: {
        Name: string;
        VisibleIndex?: string;
        GroupIndex?: string;
        SortIndex?: string;
        SortAsc?: string;
    };
};


const selectiveFlatten = (obj: any, keysToFlatten: string[]): any => { // tslint:disable-line
    if (typeof obj !== 'object') {
        return obj;
    }
    else if (Array.isArray(obj)) {
        return obj.map(item =>
            selectiveFlatten(item, keysToFlatten)
        );
    }

    return Object.keys(obj).reduce((o, key) => {
        const value = obj[key];

        // If we're not looking for the current key, we still need to recurse
        const flatten = keysToFlatten.indexOf(key) >= 0;

        return {
            ...o,
            [key]: selectiveFlatten(flatten ? value[0] : value, keysToFlatten),
        };
    }, obj);
};

const removeArraysThatDontBelong = (obj: {}): {} => {
    return selectiveFlatten(obj, [
        'Devices',
        'Usernames',
        'Resolution',
        'ExitDevices',
        'StartEnd',
        'TimeOfDay',
        'TimeInterval',
        'Sensitivity',
        'Filter',
        'TableColumns',
        'Distribution',
        'Email',
        'Recipients',
        'File',
        'FileName',
        'FileTypes',
        'Schedule',
        'DataInclusion',
        'Range',
        'Yearly',
        'Weekly',
        'Monthly',
        'Daily',
        'Hourly',
        'HeatMap',
        'Graph',
        'FilterReverseMatches',
        'ConfidenceThreshold',
        'BlockSizeMinutes',
        'BlocksBeyondCurrent',
        'TimeoutForFirstMatch',
        'HourOptions',
    ]);
};

export type Minutes = number;
export type Seconds = number;
export type Milliseconds = number;

export class ReportSettings {
    static readonly defaultFileNameFormat = '%rn_%YYYY%MM%DD_%HH%mm%ss_%fff';
    
    static readonly defaultInterval: Seconds = 3600;

    static readonly defaultStart: Minutes = -1440;
    static readonly defaultEnd: UtcTime | -1 = -1; // -1 Indicates 'now', or more specifically, 'when the report is run'
    
    static readonly defaultTimeOfDayStart: Minutes = 0;
    static readonly defaultTimeOfDayEnd: Minutes = 1440;

    static readonly defaultScheduleRangeOccurrences = 10;

    static readonly defaultScheduleDataInclusionStart: Minutes = 0;
    static readonly defaultScheduleDataInclusionEnd: Minutes = 1439;

    static readonly noDwell = -1;
    static readonly noIntersections = -1;
    static readonly defaultMinIntersections = 1;
    static readonly defaultMaxIntersections = 50;
    static readonly defaultMinDwell: Milliseconds = 5000;
    static readonly defaultMaxDwell: Milliseconds = 300000;
    static readonly defaultDwellThreshold: Milliseconds = 30000;
    
    // Face Recognition Defaults
    static readonly defaultConfidenceThreshold = 75;
    static readonly defaultBlockSizeMinutes = 20;
    static readonly defaultBlocksBeyondCurrent = 1;
    static readonly defaultTimeoutForFirstMatch = 0;
    static readonly defaultHourOptions = '';

    static readonly endNever: UtcTime = 253402300800000;
    private static readonly internalEndNever = '3155378975999999999';

    static readonly defaultFenceResolution: Size = {
        width: 320,
        height: 240,
    };
    
    private readonly obj: any; // tslint:disable-line:no-any

    static fromXml(xml: string): Readonly<ReportSettings> {
        let obj: any = {}; // tslint:disable-line
        // parseString appears to be asynchronous from the signature, but it is actually not.
        // See https://github.com/Leonidas-from-XIV/node-xml2js/issues/380
        parseString(xml, (err, result) => {
            obj = result;
        });

        // The XML parser doesn't know which nodes are supposed to be singletons and which take arrays.
        // It automatically puts arrays everywhere. So for simpliciy (and to ease serialization later),
        // patch up the object to remove arrays in places that should be singletons.
        obj = removeArraysThatDontBelong(obj);

        return new ReportSettings(obj);
    }
    
    private constructor(obj: {}) {
        this.obj = obj;
    }

    toXml(): string {
        return builder.buildObject(this.obj);
    }

    get output(): ReportOutput {
        return this.obj.Report.$.Output;
    }

    setOutput(output: ReportOutput): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { $: { Output: output } } }, this.obj)
        );
    }

    get timeInterval(): number {
        try {
            return parseInt(this.obj.Report.TimeInterval.$.Value, 10);
        }
        catch {
            return ReportSettings.defaultInterval;
        }
    }

    setTimeInterval(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { TimeInterval: { $: { Value: `${value}` } } } }, this.obj)
        );
    }

    get start(): number {
        try {
            // Start is stored as minutes since 0/0/0
            const start = parseInt(this.obj.Report.StartEnd.$.Start, 10);
            return isNaN(start)
                ? ReportSettings.defaultStart
                : start > 0
                    ? (start - epochMinutes) * millisecondsPerMinute
                    : start;
        }
        catch {
            return ReportSettings.defaultStart;
        }
    }

    setStart(start: number): Readonly<ReportSettings> {
        // Convert back to minutes since 0/0/0
        if (start > 0) {
            start = Math.round(start / millisecondsPerMinute + epochMinutes);
        }
        return new ReportSettings(
            u({ Report: { StartEnd: { $: { Start: `${start}` } } } }, this.obj)
        );
    }

    get end(): number {
        try {
            // End is stored as minutes since 0/0/0
            const end = parseInt(this.obj.Report.StartEnd.$.End, 10);
            return isNaN(end)
                ? ReportSettings.defaultEnd
                : end > 0
                    ? (end - epochMinutes) * millisecondsPerMinute
                    : end;
        }
        catch {
            return ReportSettings.defaultEnd;
        }
    }

    setEnd(end: number): Readonly<ReportSettings> {
        // Convert back to minutes since 0/0/0
        if (end > 0) {
            end = Math.round(end / millisecondsPerMinute + epochMinutes);
        }
        return new ReportSettings(
            u({ Report: { StartEnd: { $: { End: `${end}` } } } }, this.obj)
        );
    }

    get timeOfDayStart(): number {
        try {
            const start = parseInt(this.obj.Report.TimeOfDay.$.Start, 10);
            return isNaN(start) ? ReportSettings.defaultTimeOfDayStart : start;
        }
        catch {
            return ReportSettings.defaultTimeOfDayStart;
        }
    }

    setTimeOfDayStart(start: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { TimeOfDay: { $: { Start: `${start}` } } } }, this.obj)
        );
    }

    get timeOfDayEnd(): number {
        try {
            const end = parseInt(this.obj.Report.TimeOfDay.$.End, 10);
            return isNaN(end) ? ReportSettings.defaultTimeOfDayEnd : end;
        }
        catch {
            return ReportSettings.defaultTimeOfDayEnd;
        }
    }

    setTimeOfDayEnd(end: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { TimeOfDay: { $: { End: `${end}` } } } }, this.obj)
        );
    }
    
    get devices(): ReportDevice[] {
        try {
            return this.obj.Report.Devices.Device.map((d: NodeWithId) =>
                ReportDevice.fromString(d.$.ID)
            );
        }
        catch {
            return [];
        }
    }

    setDevices(devices: ReportDevice[]): Readonly<ReportSettings> {
        const existingDevices = this.devices.reduce((obj, camera) => ({ ...obj, [camera.toString()]: camera }), {});
        const acceptedDevices = devices.reduce((obj, camera) => ({ ...obj, [camera.toString()]: camera }), {});

        const cameraKeysToAdd = Object.keys(acceptedDevices).filter(key => !existingDevices[key]);
        const cameraKeysToRemove = Object.keys(existingDevices).filter(key => !acceptedDevices[key]);
        
        let settings: Readonly<ReportSettings> = this;
        settings = cameraKeysToAdd.reduce((s, key) => s.addDevice(acceptedDevices[key]), settings);
        settings = cameraKeysToRemove.reduce((s, key) => s.removeDevice(existingDevices[key].deviceIndex), settings);
        return settings;
    }

    addDevice(d: ReportDevice): Readonly<ReportSettings> {
        const deviceAdder = (devices: {}[]) => [
            ...(devices || []),
            { $: { ID: d.toString() } },
        ];
        return new ReportSettings(
            u({ Report: { Devices: { Device: deviceAdder } } }, this.obj)
        );
    }

    removeDevice(deviceIndex: DeviceIndex): Readonly<ReportSettings> {
        const re = new RegExp(`\\|${deviceIndex}$`);
        const deviceRemover = (devices: {}) => devices && u.reject((d: NodeWithId) => re.test(d.$.ID), devices);
        return new ReportSettings(
            u({ Report: { Devices: { Device: deviceRemover } } }, this.obj)
        );
    }

    get usernames(): string[] {
        try {
            return this.obj.Report.Usernames.Username.map((n: NodeWithId) => n.$.ID);
        }
        catch {
            return [];
        }
    }

    setUsernames(usernames: string[]): Readonly<ReportSettings> {
        const existingUsernames = this.usernames.reduce((obj, username) => ({ ...obj, [username]: username }), {});
        const acceptedUsernames = usernames.reduce((obj, username) => ({ ...obj, [username]: username }), {});

        const usernamesToAdd = Object.keys(acceptedUsernames).filter(key => !existingUsernames[key]);
        const usernamesToRemove = Object.keys(existingUsernames).filter(key => !acceptedUsernames[key]);

        let settings: Readonly<ReportSettings> = this;
        settings = usernamesToAdd.reduce((s, username) => s.addUsername(username), settings);
        settings = usernamesToRemove.reduce((s, username) => s.removeUsername(username), settings);
        return settings;
    }

    addUsername(username: string): Readonly<ReportSettings> {
        const usernameAdder = (items: string[]) => [
            ...(items || []),
            { $: { ID: username } },
        ];
        return new ReportSettings(
            u({ Report: { Usernames: { Username: usernameAdder } } }, this.obj)
        );
    }

    removeUsername(username: string): Readonly<ReportSettings> {
        const usernameRemover = (items?: {}) => items && u.reject((n: NodeWithId) => n.$.ID === username, items);
        return new ReportSettings(
            u({ Report: { Usernames: { Username: usernameRemover } } }, this.obj)
        );
    }

    get username(): string {
        try {
            const usernames = this.usernames || [];
            return usernames.length === 0 ? '' : usernames[0];
        }
        catch {
            return '';
        }
    }

    setUsername(username: string): Readonly<ReportSettings> {
        if (username === '') {
            return this.setUsernames([]);
        } else {
            return this.setUsernames([username]);
        }
    }
    
    get exitDevices(): ReportDevice[] {
        try {
            return this.obj.Report.ExitDevices.ExitDevice.map((d: NodeWithId) =>
                ReportDevice.fromString(d.$.ID)
            );
        }
        catch {
            return [];
        }
    }

    setExitDevices(devices: ReportDevice[]): Readonly<ReportSettings> {
        const existingDevices = this.exitDevices.reduce((obj, camera) => ({ ...obj, [camera.toString()]: camera }), {});
        const acceptedDevices = devices.reduce((obj, camera) => ({ ...obj, [camera.toString()]: camera }), {});

        const cameraKeysToAdd = Object.keys(acceptedDevices).filter(key => !existingDevices[key]);
        const cameraKeysToRemove = Object.keys(existingDevices).filter(key => !acceptedDevices[key]);
        
        let settings: Readonly<ReportSettings> = this;
        settings = cameraKeysToAdd.reduce((s, key) => s.addExitDevice(acceptedDevices[key]), settings);
        settings = cameraKeysToRemove.reduce((s, key) => s.removeExitDevice(existingDevices[key].deviceIndex), settings);
        return settings;
    }

    addExitDevice(d: ReportDevice): Readonly<ReportSettings> {
        const deviceAdder = (devices: {}[]) => [
            ...(devices || []),
            { $: { ID: d.toString() } },
        ];
        return new ReportSettings(
            u({ Report: { ExitDevices: { ExitDevice: deviceAdder } } }, this.obj)
        );
    }

    removeExitDevice(deviceIndex: DeviceIndex): Readonly<ReportSettings> {
        const re = new RegExp(`\\|${deviceIndex}$`);
        const deviceRemover = (devices: {}) => devices && u.reject((d: NodeWithId) => re.test(d.$.ID), devices);
        return new ReportSettings(
            u({ Report: { ExitDevices: { ExitDevice: deviceRemover } } }, this.obj)
        );
    }
    
    deviceFences(deviceIndex: DeviceIndex, resolution: Size): Fence[] {
        try {
            const idRegex = new RegExp(`\\|${deviceIndex}$`);
            const deviceNode =
                this.obj.Report.Devices.Device
                    .filter((d: NodeWithId) => idRegex.test(d.$.ID))
                    .shift();

            let fenceResolution = ReportSettings.defaultFenceResolution;
            try {
                fenceResolution = {
                    width: parseInt(deviceNode.Resolution.$.width, 10),
                    height: parseInt(deviceNode.Resolution.$.height, 10),
                };
            }
            catch {}

            return deviceNode.Fence.map(({ $ }: FenceNode) => ({
                x1: Math.round(parseInt($.x1, 10) * resolution.width / fenceResolution.width),
                y1: Math.round(parseInt($.y1, 10) * resolution.height / fenceResolution.height),
                x2: Math.round(parseInt($.x2, 10) * resolution.width / fenceResolution.width),
                y2: Math.round(parseInt($.y2, 10) * resolution.height / fenceResolution.height),
            }));
        }
        catch {
            return [];
        }
    }

    setDeviceFences(deviceIndex: DeviceIndex, resolution: Size, fences: Fence[]): Readonly<ReportSettings> {
        const idRegex = new RegExp(`\\|${deviceIndex}$`);
        const deviceFenceUpdater = u.map(
            u.if((d: NodeWithId) => idRegex.test(d.$.ID), u({
                Resolution: u({
                    $: resolution,
                }),
                Fence: fences.map(({ x1, y1, x2, y2 }) => ({
                    $: {
                        x1: x1.toString(),
                        y1: y1.toString(),
                        x2: x2.toString(),
                        y2: y2.toString(),
                    },
                })),
            }))
        );
        return new ReportSettings(
            u({ Report: { Devices: { Device: deviceFenceUpdater } } }, this.obj)
        );
    }

    get emailDistributionEnabled(): boolean {
        try {
            const { $ = { Disabled: false } } = this.obj.Report.Distribution.Email;
            return !$.Disabled;
        }
        catch {
            return false;
        }
    }

    setEmailDistributionEnabled(enabled: boolean): Readonly<ReportSettings> {
        if (this.emailDistributionEnabled === enabled) {
            return this;
        }
        return new ReportSettings(
            u({ Report: { Distribution: { Email:
                u.ifElse(enabled,
                    u({ $: u.omit(['Disabled']) }),
                    u({ $: { Disabled: 'True' } }),
                ),
            } } }, this.obj)
        );
    }

    get emails(): string[] {
        try {
            return this.obj.Report.Distribution.Email.Recipients.Recipient || [];
        }
        catch {
            return [];
        }
    }

    setEmails(emails: string[]): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Distribution: { Email: { Recipients: { Recipient: u.constant(emails) } } } } }, this.obj)
        );
    }

    addEmail(email: string): Readonly<ReportSettings> {
        const emailAdder = (items: string[]) => [
            ...(items || []),
            email,
        ];
        return new ReportSettings(
            u({ Report: { Distribution: { Email: { Recipients: { Recipient: emailAdder } } } } }, this.obj)
        );
    }

    removeEmail(email: string): Readonly<ReportSettings> {
        const emailRemover = (items?: {}) => items && u.reject((e: string) => e === email, items);
        return new ReportSettings(
            u({ Report: { Distribution: { Email: { Recipients: { Recipient: emailRemover } } } } }, this.obj)
        );
    }

    get emailFileTypes(): string[] {
        try {
            return this.obj.Report.Distribution.Email.FileTypes.FileType.map((fileType: ValueNode) => fileType.$.Value);
        }
        catch {
            return [];
        }
    }

    addEmailFileType(fileType: string): Readonly<ReportSettings> {
        const fileTypeAdder = (items: ValueNode[]) => [
            ...(items || []),
            { $: { Value: fileType } },
        ];
        return new ReportSettings(
            u({ Report: { Distribution: { Email: { FileTypes: { FileType: fileTypeAdder } } } } }, this.obj)
        );
    }

    removeEmailFileType(fileType: string): Readonly<ReportSettings> {
        const fileTypeRemover = (items?: {}) => items && u.reject((i: ValueNode) => fileType === i.$.Value, items);
        return new ReportSettings(
            u({ Report: { Distribution: { Email: { FileTypes: { FileType: fileTypeRemover } } } } }, this.obj)
        );
    }

    get fileDistributionEnabled(): boolean {
        try {
            const { $ = { Disabled: false } } = this.obj.Report.Distribution.File;
            return !$.Disabled;
        }
        catch {
            return false;
        }
    }

    setFileDistributionEnabled(enabled: boolean): Readonly<ReportSettings> {
        if (this.fileDistributionEnabled === enabled) {
            return this;
        }
        return new ReportSettings(
            u({ Report: { Distribution: { File:
                u.ifElse(enabled,
                    u({ $: u.omit(['Disabled']) }),
                    u({ $: { Disabled: 'True' } }),
                ),
            } } }, this.obj)
        );
    }

    get saveFileTypes(): string[] {
        try {
            return this.obj.Report.Distribution.File.FileTypes.FileType.map((fileType: ValueNode) => fileType.$.Value);
        }
        catch {
            return [];
        }
    }

    addSaveFileType(fileType: string): Readonly<ReportSettings> {
        const fileTypeAdder = (items?: ValueNode[]) => [
            ...(items || []),
            { $: { Value: fileType } },
        ];
        return new ReportSettings(
            u({ Report: { Distribution: { File: { FileTypes: { FileType: fileTypeAdder } } } } }, this.obj)
        );
    }

    removeSaveFileType(fileType: string): Readonly<ReportSettings> {
        const fileTypeRemover = (items?: {}) => items && u.reject((i: ValueNode) => fileType === i.$.Value, items);
        return new ReportSettings(
            u({ Report: { Distribution: { File: { FileTypes: { FileType: fileTypeRemover } } } } }, this.obj)
        );
    }

    get EmailCSVDelimType(): string {
        try {
            const fileTypes = this.obj.Report.Distribution.Email.FileTypes.FileType;
            if (Array.isArray(fileTypes)) {
                for (const ft of fileTypes) {
                    if (ft && ft.$ && ft.$.Value === 'CSV') {
                        // Return Delim if it exists
                        if (ft.$.Delim) {
                            return ft.$.Delim;
                        }
                        break; // Found CSV but no Delim, stop searching
                    }
                }
            }
            return ','; // Default delimiter if not found
        } catch {
            return ','; // Default on error
        }
    }
    setEmailCSVDelimType(delim: string): Readonly<ReportSettings> {

        interface FileTypeNode {
            $: {
                Value: string;
                Delim?: string;
                [key: string]: any;
            };
            [key: string]: any;
        }
        const fileTypes = this.obj.Report.Distribution.Email.FileTypes.FileType as FileTypeNode[];

        const updated = {
            ...this.obj,
            Report: {
                ...this.obj.Report,
                Distribution: {
                    ...this.obj.Report.Distribution,
                    Email: {
                        ...this.obj.Report.Distribution.Email,
                        FileTypes: {
                            FileType: fileTypes.map((ft: FileTypeNode) => {
                                if (ft.$.Value === 'CSV') {
                                    return {
                                        ...ft,
                                        $: {
                                            ...ft.$,
                                            Delim: delim,
                                        },
                                    };
                                }
                                return ft;
                            }),
                        },
                    },
                },
            },
        };

        return new ReportSettings(updated);
    }
    get FileCSVDelimType(): string {
        try {
            const fileTypes = this.obj.Report.Distribution.File.FileTypes.FileType;
            if (Array.isArray(fileTypes)) {
                for (const ft of fileTypes) {
                    if (ft && ft.$ && ft.$.Value === 'CSV') {
                        // Return Delim if it exists
                        if (ft.$.Delim) {
                            return ft.$.Delim;
                        }
                        break; // Found CSV but no Delim, stop searching
                    }
                }
            }
            return ','; // Default delimiter if not found
        } catch {
            return ','; // Default on error
        }
    }
    setFileCSVDelimType(delim: string): Readonly<ReportSettings> {

        interface FileTypeNode {
            $: {
                Value: string;
                Delim?: string;
                [key: string]: any;
            };
            [key: string]: any;
        }
        const fileTypes = this.obj.Report.Distribution.File.FileTypes.FileType as FileTypeNode[];

        const updated = {
            ...this.obj,
            Report: {
                ...this.obj.Report,
                Distribution: {
                    ...this.obj.Report.Distribution,
                    File: {
                        ...this.obj.Report.Distribution.File,
                        FileTypes: {
                            FileType: fileTypes.map((ft: FileTypeNode) => {
                                if (ft.$.Value === 'CSV') {
                                    return {
                                        ...ft,
                                        $: {
                                            ...ft.$,
                                            Delim: delim,
                                        },
                                    };
                                }
                                return ft;
                            }),
                        },
                    },
                },
            },
        };

        return new ReportSettings(updated);
    }
    get fileDestination(): string {
        try {
            return this.obj.Report.Distribution.File.$.Destination;
        }
        catch {
            return '';
        }
    }

    setFileDestination(path: string): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Distribution: { File: { $: { Destination: path } } } } }, this.obj)
        );
    }

    get fileNameFormat(): string {
        try {
            return this.obj.Report.Distribution.File.FileName.$.Format;
        }
        catch {
            return ReportSettings.defaultFileNameFormat;
        }
    }

    setFileNameFormat(format: string): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Distribution: { File: { FileName: { $: { Format: format } } } } } }, this.obj)
        );
    }

    get useDefaultFileName(): boolean {
        try {
            return this.obj.Report.Distribution.File.FileName.$.UseDefault === 'True';
        }
        catch {
            return true;
        }
    }

    setUseDefaultFileName(value: boolean): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Distribution: { File: { FileName: { $: { UseDefault: value ? 'True' : 'False' } } } } } }, this.obj)
        );
    }

    get useUtcInFileName(): boolean {
        try {
            return this.obj.Report.Distribution.File.FileName.$.UseUTCDateTime === 'True';
        }
        catch {
            return false;
        }
    }

    setUseUtcInFileName(value: boolean): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Distribution: { File: { FileName: { $: { UseUTCDateTime: value ? 'True' : 'False' } } } } } }, this.obj)
        );
    }

    get scheduleEnabled(): boolean {
        try {
            return this.obj.Report.Schedule.$.Enabled === 'True';
        }
        catch {
            return false;
        }
    }

    setScheduleEnabled(enabled: boolean): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Schedule: { $: { Enabled: enabled ? 'True' : 'False' } } } }, this.obj)
        );
    }

    get scheduleDataInclusionStart(): number {
        try {
            const start = parseInt(this.obj.Report.Schedule.DataInclusion.$.Start, 10);
            return isNaN(start) ? ReportSettings.defaultScheduleDataInclusionStart : start;
        }
        catch {
            return ReportSettings.defaultScheduleDataInclusionStart;
        }
    }

    setScheduleDataInclusionStart(start: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Schedule: { DataInclusion: { $: { Start: `${start}` } } } } }, this.obj)
        );
    }

    get scheduleDataInclusionEnd(): number {
        try {
            const end = parseInt(this.obj.Report.Schedule.DataInclusion.$.End, 10);
            return isNaN(end) ? ReportSettings.defaultScheduleDataInclusionEnd : end;
        }
        catch {
            return ReportSettings.defaultScheduleDataInclusionEnd;
        }
    }

    setScheduleDataInclusionEnd(end: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Schedule: { DataInclusion: { $: { End: `${end}` } } } } }, this.obj)
        );
    }

    get scheduleRangeStart(): number {
        try {
            if (!this.obj.Report.Schedule.Range.$.Start) {
                return 0;
            }
            const ticks = parseFloat(this.obj.Report.Schedule.Range.$.Start);
            return Math.round((ticks - epochTicks) / ticksPerMillisecond);
        }
        catch {
            return 0;
        }
    }

    setScheduleRangeStart(start: number): Readonly<ReportSettings> {
        const ticks = start * ticksPerMillisecond + epochTicks;
        return new ReportSettings(
            u({ Report: { Schedule: { Range: { $: { Start: `${ticks}` } } } } }, this.obj)
        );
    }

    get scheduleRangeEndType(): ScheduleRangeEndType {
        try {
            const occurrences = parseInt(this.obj.Report.Schedule.Range.$.Occurences, 10);
            if (!isNaN(occurrences)) {
                return 'after';
            }
            if (this.obj.Report.Schedule.Range.$.End === ReportSettings.internalEndNever) {
                return 'never';
            }
            else if (typeof this.obj.Report.Schedule.Range.$.End === 'undefined') {
                return 'never';
            }
            return 'on';
        }
        catch {
            return 'never';
        }
    }

    get scheduleRangeEnd(): number {
        try {
            if (typeof this.obj.Report.Schedule.Range.$.End === 'undefined') {
                return 0;
            }
            if (this.obj.Report.Schedule.Range.$.End === ReportSettings.internalEndNever) {
                return ReportSettings.endNever;
            }
            const ticks = parseFloat(this.obj.Report.Schedule.Range.$.End);
            return (
                isNaN(ticks)
                    ? ReportSettings.endNever
                    : Math.round((ticks - epochTicks) / ticksPerMillisecond)
            );
        }
        catch {
            return 0;
        }
    }
    
    setScheduleRangeEnd(end: number): Readonly<ReportSettings> {
        const ticks =
            end === ReportSettings.endNever
                ? ReportSettings.internalEndNever
                : end * ticksPerMillisecond + epochTicks;

        const noOccurrences = u({ Report: { Schedule: { Range: { $: u.omit(['Occurences']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Range: { $: { End: `${ticks}` } } } } }, noOccurrences)
        );
    }

    get scheduleRangeOccurrences(): number {
        try {
            return parseInt(this.obj.Report.Schedule.Range.$.Occurences, 10);
        }
        catch {
            return ReportSettings.defaultScheduleRangeOccurrences;
        }
    }

    setScheduleRangeOccurrences(value: number): Readonly<ReportSettings> {
        const noEnd = u({ Report: { Schedule: { Range: { $: u.omit(['End']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Range: { $: { Occurences: value } } } } }, noEnd)
        );
    }

    get scheduleType(): ScheduleType {
        try {
            const { Hourly, Daily, Weekly, Monthly, Yearly } = this.obj.Report.Schedule;
            if (Hourly) return 'Hourly';
            if (Daily) return 'Daily';
            if (Weekly) return 'Weekly';
            if (Monthly) return 'Monthly';
            if (Yearly) return 'Yearly';
        }
        catch {}
        return 'Daily';
    }

    setScheduleType(type: ScheduleType): Readonly<ReportSettings> {
        const objWithoutScheduleTypes = u({ Report: { Schedule: u.omit(ScheduleTypes) } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { [type]: { $: { Frequency: 1 } } } } }, objWithoutScheduleTypes)
        );
    }

    get scheduleFrequency(): number {
        try {
            const { Hourly, Daily, Weekly, Monthly, Yearly } = this.obj.Report.Schedule;
            return parseInt((Hourly || Daily || Weekly || Monthly || Yearly).$.Frequency, 10);
        }
        catch {
            return 1;
        }
    }

    setScheduleFrequency(frequency: number): Readonly<ReportSettings> {
        const scheduleType = this.scheduleType;
        return new ReportSettings(
            u({ Report: { Schedule: { [scheduleType]: { $: { Frequency: `${frequency}` } } } } }, this.obj)
        );
    }

    get scheduleWeekdays(): boolean {
        try {
            return this.obj.Report.Schedule.Daily.$.WeekDays === 'true';
        }
        catch {
            return false;
        }
    }

    setScheduleWeekdays(weekdays: boolean, frequency: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Schedule: { Daily:
                u.ifElse(
                    weekdays,
                    u.constant({ $: { WeekDays: 'true' } }),
                    u.constant({ $: { Frequency: `${frequency}` } })
                ),
            } } }, this.obj)
        );
    }

    get scheduleDays(): boolean[] {
        try {
            const { Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday } = this.obj.Report.Schedule.Weekly.$;
            return [
                Sunday === 'True',
                Monday === 'True',
                Tuesday === 'True',
                Wednesday === 'True',
                Thursday === 'True',
                Friday === 'True',
                Saturday === 'True',
            ];
        }
        catch {
            return [ true, true, true, true, true, true, true ];
        }
    }

    setScheduleDays(days: boolean[]): Readonly<ReportSettings> {
        const [ sunday, monday, tuesday, wednesday, thursday, friday, saturday ] = days;
        return new ReportSettings(
            u({ Report: { Schedule: { Weekly: { $: {
                Sunday: sunday ? 'True' : 'False',
                Monday: monday ? 'True' : 'False',
                Tuesday: tuesday ? 'True' : 'False',
                Wednesday: wednesday ? 'True' : 'False',
                Thursday: thursday ? 'True' : 'False',
                Friday: friday ? 'True' : 'False',
                Saturday: saturday ? 'True' : 'False',
            } } } } }, this.obj)
        );
    }

    get useScheduleMonthlyDayNum(): boolean {
        try {
            // If 'Day' is absent then we're using DayNum
            return typeof this.obj.Report.Schedule.Monthly.$.Day === 'undefined';
        }
        catch {
            return true;
        }
    }

    get scheduleMonthlyDayNum(): number {
        try {
            return parseInt(this.obj.Report.Schedule.Monthly.$.DayNum, 10) || 1;
        }
        catch {
            return 1;
        }
    }

    setScheduleMonthlyDayNum(day: number): Readonly<ReportSettings> {
        const noDayAndPosition = u({ Report: { Schedule: { Monthly: { $: u.omit(['Day', 'Position']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Monthly: { $: { DayNum: `${day}` } } } } }, noDayAndPosition)
        );
    }

    get scheduleMonthlyDay(): string {
        try {
            return this.obj.Report.Schedule.Monthly.$.Day || 'Sunday';
        }
        catch {
            return 'Sunday';
        }
    }

    setScheduleMonthlyDay(day: string): Readonly<ReportSettings> {
        const noDayNum = u({ Report: { Schedule: { Monthly: { $: u.omit(['DayNum']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Monthly: { $: { Day: `${day}` } } } } }, noDayNum)
        );
    }

    get scheduleMonthlyPosition(): string {
        try {
            return this.obj.Report.Schedule.Monthly.$.Position || 'first';
        }
        catch {
            return 'first';
        }
    }

    setScheduleMonthlyPosition(value: string): Readonly<ReportSettings> {
        const noDayNum = u({ Report: { Schedule: { Monthly: { $: u.omit(['DayNum']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Monthly: { $: { Position: value } } } } }, noDayNum)
        );
    }

    get useScheduleYearlyDayNum(): boolean {
        try {
            // If 'Day' is absent then we're using DayNum
            return typeof this.obj.Report.Schedule.Yearly.$.Day === 'undefined';
        }
        catch {
            return true;
        }
    }

    get scheduleYearlyDayNum(): number {
        try {
            return parseInt(this.obj.Report.Schedule.Yearly.$.DayNum, 10) || 1;
        }
        catch {
            return 1;
        }
    }

    setScheduleYearlyDayNum(day: number): Readonly<ReportSettings> {
        const noDayAndPosition = u({ Report: { Schedule: { Yearly: { $: u.omit(['Day', 'Position']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Yearly: { $: { DayNum: `${day}` } } } } }, noDayAndPosition)
        );
    }

    get scheduleYearlyDay(): string {
        try {
            return this.obj.Report.Schedule.Yearly.$.Day || 'Sunday';
        }
        catch {
            return 'Sunday';
        }
    }

    setScheduleYearlyDay(day: string): Readonly<ReportSettings> {
        const noDayNum = u({ Report: { Schedule: { Yearly: { $: u.omit(['DayNum']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Yearly: { $: { Day: day } } } } }, noDayNum)
        );
    }

    get scheduleYearlyMonth(): string {
        try {
            return this.obj.Report.Schedule.Yearly.$.Month || 'January';
        }
        catch {
            return 'January';
        }
    }

    setScheduleYearlyMonth(month: string): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Schedule: { Yearly: { $: { Month: month } } } } }, this.obj)
        );
    }

    get scheduleYearlyPosition(): string {
        try {
            return this.obj.Report.Schedule.Yearly.$.Position || 'first';
        }
        catch {
            return 'first';
        }
    }

    setScheduleYearlyPosition(value: string): Readonly<ReportSettings> {
        const noDayNum = u({ Report: { Schedule: { Yearly: { $: u.omit(['DayNum']) } } } }, this.obj);
        return new ReportSettings(
            u({ Report: { Schedule: { Yearly: { $: { Position: value } } } } }, noDayNum)
        );
    }

    get graphSize(): Size {
        try {
            const match = /^(\d+),(\d+)$/.exec(this.obj.Report.Graph.$.Size);
            if (match) {
                return {
                    width: parseInt(match[1], 10),
                    height: parseInt(match[2], 10),
                };
            }
        }
        catch {}
        return { width: 800, height: 600 };
    }

    setGraphSize(width: number, height: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Graph: { $: { Size: `${width},${height}` } } } }, this.obj)
        );
    }

    get graphType(): GraphType {
        try {
            return this.obj.Report.Graph.$.Type || 'Line';
        }
        catch {
            return 'Line';
        }
    }

    setGraphType(type: GraphType): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Graph: { $: { Type: type } } } }, this.obj)
        );
    }

    get graphDefaultData(): string | undefined {
        try {
            return this.obj.Report.Graph.$.DefaultData;
        }
        catch {
            return undefined;
        }
    }

    setGraphDefaultData(value: string): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Graph: { $: { DefaultData: value } } } }, this.obj)
        );
    }

    get minIntersections(): number {
        try {
            return parseInt(this.obj.Report.Sensitivity.$.MinIntersec, 10) || -1;
        }
        catch {
            return -1;
        }
    }

    setMinIntersections(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Sensitivity: { $: { MinIntersec: `${value}` } } } }, this.obj)
        );
    }

    get maxIntersections(): number {
        try {
            return parseInt(this.obj.Report.Sensitivity.$.MaxIntersec, 10) || -1;
        }
        catch {
            return -1;
        }
    }

    setMaxIntersections(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Sensitivity: { $: { MaxIntersec: `${value}` } } } }, this.obj)
        );
    }

    get minDwell(): number {
        try {
            let time = parseInt(this.obj.Report.Sensitivity.$.MinDwell, 10);
            if (isNaN(time)) {
                time = -1;
            }
            return time;
        }
        catch {
            return -1;
        }
    }

    setMinDwell(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Sensitivity: { $: { MinDwell: `${value}` } } } }, this.obj)
        );
    }

    get maxDwell(): number {
        try {
            let time = parseInt(this.obj.Report.Sensitivity.$.MaxDwell, 10);
            if (isNaN(time)) {
                time = -1;
            }
            return time;
        }
        catch {
            return -1;
        }
    }

    setMaxDwell(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Sensitivity: { $: { MaxDwell: `${value}` } } } }, this.obj)
        );
    }

    get dwellThreshold(): number {
        try {
            let time = parseInt(this.obj.Report.Sensitivity.$.DwellThreshold, 10);
            if (isNaN(time)) {
                time = -1;
            }
            return time;
        }
        catch {
            return -1;
        }
    }

    setDwellThreshold(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { Sensitivity: { $: { DwellThreshold: `${value}` } } } }, this.obj)
        );
    }

    get filter(): number[] | null {
        try {
            const value: string = this.obj.Report.Filter.$.Value;
            if (value === '') {
                return [];
            }
            return value.split(',').map(s => parseInt(s, 10));
        }
        catch {
            return null;
        }
    }

    setFilter(listIds: number[] | null): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report:
                u.ifElse(listIds !== null,
                    u({ Filter: { $: { Column: 'ID', Value: () => listIds!.join(',') } } }),
                    u.omit(['Filter']),
                ),
            }, this.obj)
        );
    }

    removeHeatMap(): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report:
                u.omit(['HeatMap']),
            }, this.obj)
        );
    }

    get heatMapType(): HeatMapType {
        try {
            return this.obj.Report.HeatMap.$.Default || 'Paths';
        }
        catch {
            return 'Paths';
        }
    }

    setHeatMapImage(base64Image: string | null): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { HeatMap:
                u.ifElse(base64Image,
                    u({ $: { Image: base64Image } }),
                    u({ $: u.omit(['Image']) }),
                ),
            } }, this.obj)
        );
    }

    get heatMapImage(): string | null {
        try {
            return this.obj.Report.HeatMap.$.Image || null;
        }
        catch {
            return null;
        }
    }

    setHeatMapType(type: HeatMapType): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { HeatMap: { $: { Default: type } } } }, this.obj)
        );
    }

    get heatMapZones(): ReadonlyArray<HeatMapZone> {
        try {
            const zones: string[] = this.obj.Report.HeatMap.$.Zones.split(';');
            return zones.map(HeatMapZone.fromString);
        }
        catch {
            return [];
        }
    }

    addHeatMapZone(zone: HeatMapZone): Readonly<ReportSettings> {
        const zones = [ ...this.heatMapZones, zone ];
        const zonesString = zones.map(z => z.toString()).join(';');
        return new ReportSettings(
            u({ Report: { HeatMap: { $: { Zones: zonesString } } } }, this.obj)
        );
    }

    removeHeatMapZone(name: string): Readonly<ReportSettings> {
        const zones = this.heatMapZones.filter(z => z.name !== name);
        const zonesString = zones.map(z => z.toString()).join(';');
        return new ReportSettings(
            u({ Report: { HeatMap: { $: { Zones: zonesString } } } }, this.obj)
        );
    }

    get tableColumns(): ReadonlyArray<TableColumn> {
        try {
            return this.obj.Report.TableColumns.TableColumn.map(({ $ }: TableColumnNode) => ({
                name: $.Name,
                visibleIndex: $.VisibleIndex ? parseInt($.VisibleIndex, 10) : undefined,
                groupIndex: $.GroupIndex ? parseInt($.GroupIndex, 10) : undefined,
                sortIndex: $.SortIndex ? parseInt($.SortIndex, 10) : undefined,
                sortAsc: $.SortAsc ? $.SortAsc === 'True' : undefined,
            }));
        }
        catch {
            return [];
        }
    }

    setTableColumns(columns: ReadonlyArray<TableColumn>): Readonly<ReportSettings> {
        const isUndefined = (value: {} | undefined, key: string) =>
            typeof value === 'undefined'
        ;
        const value = columns.map(c => ({
            $: u.omitBy(isUndefined, {
                Name: c.name,
                VisibleIndex: typeof c.visibleIndex === 'undefined' ? undefined : `${c.visibleIndex}`,
                GroupIndex: typeof c.groupIndex === 'undefined' ? undefined : `${c.groupIndex}`,
                SortIndex: typeof c.sortIndex === 'undefined' ? undefined : `${c.sortIndex}`,
                SortAsc: typeof c.sortAsc === 'undefined' ? undefined : c.sortAsc ? 'True' : 'False',
            }),
        }));
        return new ReportSettings(
            u({ Report: { TableColumns: u.constant({ TableColumn: value }) } }, this.obj)
        );
    }

    get filterReverseMatches(): boolean {
        try {
            return this.obj.Report.FilterReverseMatches.$.Value === 'True';
        }
        catch {
            return true;
        }
    }
    
    setFilterReverseMatches(enabled: boolean): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { FilterReverseMatches: { $: { Value: enabled ? 'True' : 'False' } } } }, this.obj)
        );
    }

    get confidenceThreshold(): number {
        try {
            const value = parseInt(this.obj.Report.ConfidenceThreshold.$.Value, 10);
            if (!isNaN(value)) {
                return value;
            }
        }
        catch {}
        return ReportSettings.defaultConfidenceThreshold;
    }
    
    setConfidenceThreshold(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { ConfidenceThreshold: { $: { Value: `${value}` } } } }, this.obj)
        );
    }

    get blockSizeMinutes(): number {
        try {
            const value = parseInt(this.obj.Report.BlockSizeMinutes.$.Value, 10);
            if (!isNaN(value)) {
                return value;
            }
        }
        catch {}
        return ReportSettings.defaultBlockSizeMinutes;
    }
    
    setBlockSizeMinutes(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { BlockSizeMinutes: { $: { Value: `${value}` } } } }, this.obj)
        );
    }

    get blocksBeyondCurrent(): number {
        try {
            const value = parseInt(this.obj.Report.BlocksBeyondCurrent.$.Value, 10);
            if (!isNaN(value)) {
                return value;
            }
        }
        catch {}
        return ReportSettings.defaultBlocksBeyondCurrent;
    }
    
    setBlocksBeyondCurrent(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { BlocksBeyondCurrent: { $: { Value: `${value}` } } } }, this.obj)
        );
    }

    get timeoutForFirstMatch(): number {
        try {
            const value = parseInt(this.obj.Report.TimeoutForFirstMatch.$.Value, 10);
            if (!isNaN(value)) {
                return value;
            }
        }
        catch {}
        return ReportSettings.defaultTimeoutForFirstMatch;
    }
    
    setTimeoutForFirstMatch(value: number): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { TimeoutForFirstMatch: { $: { Value: `${value}` } } } }, this.obj)
        );
    }

    get hourOptions(): string {
        try {
            return this.obj.Report.HourOptions.$.Value;
        }
        catch {
            return ReportSettings.defaultHourOptions;
        }
    }
    
    setHourOptions(value: string): Readonly<ReportSettings> {
        return new ReportSettings(
            u({ Report: { HourOptions: { $: { Value: value } } } }, this.obj)
        );
    }
}



// WEBPACK FOOTER //
// ./src/logic/reports/ReportSettings.ts